import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react'
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** One PDF page at a time with paging and zoom (demo `pdf-preview.tsx`). */
export function PdfPreview({
  url,
  name,
  viewportClassName = 'max-h-[62dvh]',
}: {
  url: string
  name: string
  viewportClassName?: string
}) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const container = useRef<HTMLDivElement>(null)
  const [document, setDocument] = useState<{ url: string; pdf: PDFDocumentProxy } | null>(null)
  const [page, setPage] = useState(1)
  const [zoom, setZoom] = useState(1)
  const [width, setWidth] = useState(0)
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0))
    observer.observe(container.current!)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    let disposed = false
    let loading: PDFDocumentLoadingTask | undefined
    setDocument(null)
    setPage(1)
    setZoom(1)
    setStatus('loading')
    void import('pdfjs-dist')
      .then(async (pdfjs) => {
        if (disposed) return
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
        loading = pdfjs.getDocument({ url })
        const pdf = await loading.promise
        if (!disposed) setDocument({ url, pdf })
      })
      .catch(() => {
        if (!disposed) setStatus('error')
      })
    return () => {
      disposed = true
      void loading?.destroy()
    }
  }, [url, attempt])

  useEffect(() => {
    if (!document || document.url !== url || !width) return
    let disposed = false
    let rendering: RenderTask | undefined
    const target = canvas.current!
    setStatus('loading')
    void document.pdf
      .getPage(page)
      .then(async (pdfPage) => {
        if (disposed) return
        const scale = (width / pdfPage.getViewport({ scale: 1 }).width) * zoom
        const viewport = pdfPage.getViewport({ scale })
        const density = Math.min(window.devicePixelRatio || 1, 2)
        target.width = Math.ceil(viewport.width * density)
        target.height = Math.ceil(viewport.height * density)
        target.style.width = `${viewport.width}px`
        target.style.height = `${viewport.height}px`
        rendering = pdfPage.render({ canvas: target, viewport, transform: [density, 0, 0, density, 0, 0] })
        await rendering.promise
        if (!disposed) setStatus('ready')
      })
      .catch(() => {
        if (!disposed) setStatus('error')
      })
    return () => {
      disposed = true
      rendering?.cancel()
      target.width = 0
      target.height = 0
    }
  }, [document, url, page, zoom, width])

  const pages = document?.url === url ? document.pdf.numPages : 0
  return (
    <div className="min-w-0" data-pdf-state={status}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-1 text-sm">
        <div className="flex items-center gap-1">
          <Button aria-label="Previous page" disabled={!pages || page <= 1} onClick={() => setPage(page - 1)} size="icon" variant="ghost">
            <ChevronLeft aria-hidden="true" />
          </Button>
          <span aria-live="polite" className="min-w-24 text-center tabular-nums">
            {pages ? `Page ${page} of ${pages}` : 'PDF'}
          </span>
          <Button aria-label="Next page" disabled={!pages || page >= pages} onClick={() => setPage(page + 1)} size="icon" variant="ghost">
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button aria-label="Zoom out" disabled={!pages || zoom <= 1} onClick={() => setZoom(zoom - 0.25)} size="icon" variant="ghost">
            <Minus aria-hidden="true" />
          </Button>
          <span className="min-w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <Button aria-label="Zoom in" disabled={!pages || zoom >= 3} onClick={() => setZoom(zoom + 0.25)} size="icon" variant="ghost">
            <Plus aria-hidden="true" />
          </Button>
        </div>
      </div>
      {status === 'loading' ? (
        <p className="mb-2 text-sm text-muted-foreground" role="status">
          Loading PDF…
        </p>
      ) : null}
      {status === 'error' ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-destructive" role="alert">
          Could not open this PDF. Retry or download the file.
          <Button onClick={() => setAttempt(attempt + 1)} size="sm" variant="outline">
            Retry
          </Button>
        </div>
      ) : null}
      <div
        aria-busy={status === 'loading'}
        aria-label={`${name} pages`}
        className={cn(
          'overflow-auto rounded-md border bg-muted/40 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
          viewportClassName,
        )}
        ref={container}
        role="region"
        tabIndex={0}
      >
        <canvas
          aria-label={`${name}, page ${page}${pages ? ` of ${pages}` : ''}`}
          className={status === 'ready' ? 'block' : 'invisible block'}
          ref={canvas}
          role="img"
        />
      </div>
    </div>
  )
}
